<template>
  <component
    :is="routerLink ? 'router-link' : 'div'"
    :to="routerLink || null"
    :class="[
      $style.base,
      {
        [$style.zebra]: zebra,
        [$style.isLink]: !!routerLink || clickable,
        [$style.isFooter]: isFooter,
        [$style.disabled]: disabled,
        [$style.smallScreen]: $screen === 's',
        [$style.breakLines]: breakLines,
        [$style.hasFullWidthCell]: cells.some((cell) => cell && cell.fullWidth)
      }
    ]"
    @click="$emit('click')"
  >
    <td
      v-if="selectable"
      :class="[$style.cell, $style.checkboxCell]"
      @click="isHeader ? undefined : onCheckboxClick($event)"
      v-test="isHeader ? undefined : '_base-table-checkbox'"
    >
      <div
        v-if="!isHeader"
        :class="$style.cellContent"
      >
        <div :class="$style.checkbox">
          <BaseCheckbox :modelValue="selectedValue" />
        </div>
      </div>
    </td>
    <component
      :is="isFooter ? 'td' : 'div'"
      v-for="(cell, index) in formattedCells"
      v-show="!cell.hide"
      :key="`cell-${index}`"
      :class="[
        $style.cell,
        {
          [$style.alignRight]: cell.alignRight,
          [$style.center]: cell.center,
          [$style.iconOnly]: !cell.editable && (typeof cell.value === 'boolean' || !cell.value),
          [$style.hasIcon]: !!cell.icon,
          [$style.clickable]: cell.clickable,
          [$style.editable]: cell.editable,
          [$style.fullWidth]: cell.fullWidth,
          [$style.printCompact]: printCompact
        }
      ]"
      :colspan="colspan"
      v-test="'_base-table-cell'"
      :v-component="cell.component"
    >
      <div :class="$style.cellContent">
        <BaseLabel
          v-if="cell.label"
          :state="typeof cell.label === 'string' ? cell.label : undefined"
          :color="typeof cell.label === 'object' ? cell.label.color : undefined"
          v-test="'_base-table-label'"
        >
          <template v-if="typeof cell.label === 'object'">
            {{ cell.label.text }}
          </template>
        </BaseLabel>
        <BaseIcon
          v-else-if="cell.value === true"
          name="check"
          :tooltip="
            cell.tooltip
              ? {
                  text: cell.tooltip,
                  touch: true
                }
              : null
          "
          v-test="'_base-table-checkmark'"
        />
        <BaseInput
          v-else-if="cell.editable"
          v-model="cell.value"
          :type="cell.type"
          :required="cell.required"
          :minValue="cell.minValue"
          :maxValue="cell.maxValue"
          :disabled="disabled"
          @update:modelValue="(value: any) => $emit('valueChange', value)"
        />
        <BaseHeading
          v-else-if="isHeader"
          inline
          noWrap
          :size="$screen === 's' ? 'xs' : 's'"
          v-test="'_base-table-text'"
        >
          {{ cell.value || '' }}
        </BaseHeading>
        <div
          v-else-if="cell.html"
          v-html="cell.html"
          v-test="'_base-table-html'"
        />
        <BaseText
          v-else-if="!cell.icon || typeof cell.value === 'string'"
          :bold="isFooter"
          :oneLine="!breakLines"
          :color="cell.color || 'default'"
          v-test="'_base-table-text'"
        >
          <s v-if="cell.strikethrough">{{ cell.value || '-' }}</s>
          <template v-else>{{ cell.value || '-' }}</template>
        </BaseText>
        <div
          v-if="cell.icon"
          :class="$style.icon"
          @click="$emit('clickCell')"
        >
          <BaseIcon
            :color="cell.color || (cell.clickable ? 'primary' : 'default')"
            :name="cell.icon"
            :size="cell.clickable ? 'm' : 's'"
            :tooltip="cell.tooltip"
            :disabled="cell.disabled"
            v-test="`_base-table-icon-${cell.icon}`"
          />
        </div>
        <div
          v-if="sortOrder && cell.sort"
          :class="$style.sorting"
        >
          <div
            :class="[
              $style.sortingInner,
              {
                [$style.ascending]:
                  sortOrder.sortBy === cell.sort &&
                  sortOrder.direction === 'ASC',
                [$style.descending]:
                  sortOrder.sortBy === cell.sort &&
                  sortOrder.direction === 'DESC'
              }
            ]"
            @click="onSortClick(cell.sort)"
            v-test="'_base-table-sort'"
          >
            <BaseIcon
              name="chevron-up"
              :color="
                sortOrder.sortBy === cell.sort && sortOrder.direction === 'ASC'
                  ? 'primary'
                  : 'secondary'
              "
            />
            <BaseIcon
              name="chevron-down"
              :color="
                sortOrder.sortBy === cell.sort && sortOrder.direction === 'DESC'
                  ? 'primary'
                  : 'secondary'
              "
            />
          </div>
        </div>
      </div>
    </component>
  </component>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  inheritAttrs: false,
  props: {
    cells: {
      type: Array,
      required: true,
      validator: (value) =>
        !value.find((cell) => cell === '-' || cell?.value === '-'),
    },
    routerLink: {
      type: [Object, String],
    },
    isHeader: {
      type: Boolean,
      default: false,
    },
    isFooter: {
      type: Boolean,
      default: false,
    },
    zebra: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    breakLines: {
      type: Boolean,
      default: false,
    },
    printCompact: {
      type: Boolean,
      default: false,
    },
    sortOrder: [Object, Number],
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click', 'valueChange', 'clickCell', 'sort', 'select'],
  computed: {
    formattedCells() {
      return this.cells.map((cell) =>
        cell && typeof cell === 'object' ? cell : { value: cell },
      );
    },
    colspan() {
      // When the footer has only one item, we want to display the content over the entire width of the table.
      // The only way to do this inside a table layout, is to set the colspan to a very high value, since there is no such thing as "100%", and also the amount of columns is variable.
      return this.isFooter && this.cells.length === 1 ? 100 : null;
    },
    selectedValue: {
      get() {
        return this.selected;
      },
      set(value) {
        this.$emit('select', value);
      },
    },
  },
  methods: {
    onSortClick(sortBy: string) {
      let direction = this.sortOrder.direction;

      if (this.sortOrder.sortBy === sortBy) {
        direction = direction === 'ASC' ? 'DESC' : 'ASC';
      }

      this.$emit('sort', {
        sortBy,
        direction,
      });
    },
    onCheckboxClick(e) {
      if (this.disabled) {
        return;
      }

      this.selectedValue = !this.selectedValue;
      e.preventDefault();
    },
  },
});
</script>

<style
  lang="scss"
  module
>
$cellHeight: 55px;
.base {
  display: table-row;
  background-color: var(--surface-primary);

  // Opaque hover and press steps, so zebra rows and the sticky actions
  // column behind them stay solid. No transition here: the rows sit in a
  // TransitionGroup, which reads it as a leave animation and keeps deleted
  // rows in the DOM
  &.isLink {
    cursor: pointer;

    @include hover {
      background-color: var(--surface-tertiary) !important;
    }

    &:active {
      background-color: var(--surface-tertiary-strong) !important;
    }
  }
  &.zebra {
    &:nth-child(even) {
      background-color: var(--surface-secondary);
    }
  }
  &.isFooter {
    background-color: var(--surface-secondary);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    user-select: none;
  }
}

.cell {
  display: table-cell;
  padding: $spacing;
  height: $cellHeight;
  max-width: 300px;

  &.alignRight {
    text-align: right;
  }

  &.iconOnly {
    vertical-align: middle;
    padding-top: 13px;
    padding-bottom: 13px;
  }

  .base:not(:last-child) & {
    border-bottom: 1px solid var(--border);
  }

  &.editable {
    min-width: 150px;
  }

  &:not(.fullWidth) {
    .base.hasFullWidthCell & {
      width: 1%;
      white-space: nowrap;
    }
  }

  @media print {
    &.printCompact {
      padding: $spacing * 0.5;
      height: auto;
    }
  }
}

.cellContent {
  display: flex;
  align-items: center;

  .cell.center & {
    justify-content: center;
  }

  .cell.alignRight & {
    justify-content: flex-end;
  }

  @media print {
    .cell.printCompact & > * {
      font-size: 10px;
    }
  }

  & > * {
    &:not(:first-child) {
      margin-left: $spacing;
    }
  }
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;

  .cell.clickable & {
    padding: 4px;
    cursor: pointer;
  }
}

.sorting {
  @include background-hover;
  padding: 6px 0;
}

.sortingInner {
  position: absolute;
  top: -2px;
  left: -10px;
  display: flex;
  align-items: center;
  flex-direction: column;
  z-index: 1;

  & > * {
    margin: -6px 0;
  }

  &.ascending > *:last-child,
  &.descending > *:first-child {
    opacity: 0.5;
  }
}

.checkbox {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none; // Disabling the checkbox itself because toggling happens when clicking on the cell, to give the user a large area to click on
}

.checkboxCell {
  position: relative;
  width: 48px;
}
</style>
